// Package Imports
/* eslint-disable max-lines */
import { Col, Form, Row, Select, DatePicker, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTable } from '@refinedev/antd';
import { useWatch } from 'antd/es/form/Form';

// Project Imports
import DeleteButtonSvg from 'assets/images/DeleteButton';
import apiEndpoint from 'core/common/apiEndPoints';
import { useTranslate } from '@refinedev/core';

const AttributeReviewForm = ({
  formData,
  stateFunctionHandler,
  rulePlanId,
  rulePlanList = [],
  onRulePlanChange,
  programConfigId,
  workflowList = [],
  onWorkflowChange,
}) => {
  const { form } = formData;
  const { setFilters } = stateFunctionHandler;
  const { Option } = Select;
  const { RangePicker } = DatePicker;
  const t = useTranslate();
  const filtersResource = `${apiEndpoint.getAllfilters}/?rule_plan_id=${rulePlanId}${
    workflowList.length > 1 && programConfigId
      ? `&program_config_id=${programConfigId}`
      : ''
  }`;

  const { tableProps: filterListProps } = useTable({
    resource: filtersResource,
    dataProviderName: 'payoutProvider',
    pagination: { mode: 'off' },
    syncWithLocation: false,
    queryOptions: {
      enabled: !!rulePlanId && (workflowList.length <= 1 || !!programConfigId),
    },
  });

  const safeFilterSource = Array.isArray(filterListProps?.dataSource)
    ? filterListProps.dataSource[0]
    : (filterListProps?.dataSource ?? {});
  const filterList = safeFilterSource.filters || [];
  const filterKeys = safeFilterSource.fieldKeys || [];
  const handleApply = async () => {
    try {
      const values = await form.validateFields();
      const cleanedFieldFilters = (values.fieldFilters || []).filter(
        (f: any) => f.fieldKey && f.fieldValue?.length
      );

      const payload = {
        ...values,
        fieldFilters: cleanedFieldFilters,
      };

      setFilters(payload);
    } catch (errorInfo) {
      console.log('Validation failed:', errorInfo);
    }
  };
  // prettier-ignore
  const formKeyChange = useWatch('fieldFilters', { form, preserve: true });
  const handleReset = () => {
    form.resetFields();
  };
  return (
    <Form
      form={form}
      layout="vertical"
      scrollToFirstError
      onValuesChange={() => {}}
    >
      <Row gutter={[24, 0]}>
        {workflowList.length > 0 ? (
          <Col span={8}>
            <Form.Item
              label="Payout Configuration list"
              name="workflow"
              required
              className="custom-dropdown"
              rules={[
                {
                  required: true,
                  message: 'Please select a Payout Configuration',
                },
              ]}
            >
              <Select
                allowClear
                showSearch
                placeholder="Select payout configuration list"
                className="custom-dropdown"
                onChange={(val) => {
                  if (workflowList.length > 1) {
                    onWorkflowChange(val); // triggers rule plan fetch with this program_config_id
                  }
                  form.setFieldValue('workflow', val);
                }}
              >
                {workflowList.map((wf) => (
                  <Option
                    key={wf.program_config_id}
                    value={wf.program_config_id}
                  >
                    {wf.program_config_name || wf.workflow_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        ) : null}
        {rulePlanList.length > 0 ? (
          <Col span={8}>
            <Form.Item
              label="Rule Plan"
              name="rulePlan"
              required
              className="custom-dropdown"
              rules={[{ required: true, message: 'Please select a rule plan' }]}
            >
              <Select
                allowClear
                showSearch
                placeholder="Select rule plan"
                className="custom-dropdown"
                onChange={(val) => {
                  onRulePlanChange(val);
                  form.setFieldValue('rulePlan', val);
                }}
              >
                {rulePlanList.map((plan) => (
                  <Option key={plan.rule_plan_id} value={plan.rule_plan_id}>
                    {plan.rule_plan_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        ) : null}
      </Row>
      <Row gutter={[24, 0]}>
        <Col span={8}>
          <Form.Item
            name="dateRange"
            label="Date Range"
            rules={[{ required: true, message: 'Date range is required' }]}
            hasFeedback
          >
            <RangePicker className="a8-date-picker" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="differentiator"
            label="Slice Data On"
            className="custom-dropdown"
            rules={[
              {
                required: true,
                message: 'Please select a key for data slice on',
              },
            ]}
            hasFeedback
          >
            <Select
              allowClear
              showSearch
              placeholder={t('placeholder.selectKeyForData')}
              className="custom-dropdown"
            >
              {filterKeys.map((key) => (
                <Option key={key} value={key}>
                  {key}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="compareKey"
            label="Values To Plot"
            className="custom-dropdown"
            rules={[
              { required: true, message: 'Please select a key to compare' },
            ]}
            hasFeedback
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder={t('placeholder.compareKey')}
              className="multi-select"
            >
              {filterKeys.map((key) => (
                <Option key={key} value={key}>
                  {key}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.List name="fieldFilters" key={formKeyChange}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => {
              const selectedKey = form.getFieldValue([
                'fieldFilters',
                name,
                'fieldKey',
              ]);
              const valuesForKey = selectedKey ? filterList[selectedKey] : [];

              return (
                <Row
                  gutter={[24, 0]}
                  key={key}
                  // align="middle"
                  style={{ marginBottom: 8 }}
                >
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'fieldKey']}
                      rules={[]}
                      label="Field"
                      className="custom-dropdown"
                    >
                      <Select
                        showSearch
                        placeholder={t('placeholder.filedKey')}
                        className="custom-dropdown"
                      >
                        {filterKeys.map((filterKey) => (
                          <Option key={filterKey} value={filterKey}>
                            {filterKey}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'fieldValue']}
                      rules={[]}
                      label="Field Value"
                      className="custom-dropdown"
                    >
                      <Select
                        mode="multiple"
                        placeholder={t('placeholder.filedValue')}
                        allowClear
                        showSearch
                        disabled={!selectedKey}
                        className="multi-select"
                      >
                        {valuesForKey?.map((value: string) => (
                          <Option key={value} value={value}>
                            {value}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Button
                      // className="mt-xl"
                      className="a8-delete-button mt-4xl"
                      danger
                      onClick={() => remove(name)}
                      icon={<DeleteButtonSvg />}
                    />
                    {/* <DeleteFilled /> */}
                    {/* </Button> */}
                  </Col>
                </Row>
              );
            })}
            <Row justify="center">
              <Form.Item>
                <Button
                  className="secondary-btn"
                  icon={<PlusOutlined />}
                  onClick={() => add()}
                >
                  {t('buttons.addFilter')}
                </Button>
              </Form.Item>
            </Row>
          </>
        )}
      </Form.List>
      <Row justify="end" gutter={8}>
        <Col>
          <Button onClick={handleReset} className="btn-width">
            {t('buttons.reset')}
          </Button>
        </Col>
        <Col>
          <Button type="primary" onClick={handleApply} className="btn-width">
            {t('buttons.apply')}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default AttributeReviewForm;
